/* -----------------------------------------------------------------------
 * Immutable, centralised API-path builder – SINGLE source of truth.
 * -------------------------------------------------------------------- */

import { logDebug } from '@/utils/errorHandler';

export const API_BASE = '/api'; //  ← never edit elsewhere

const join = (...parts: (string | number)[]) =>
  parts
    .filter(Boolean)
    .map(p => p.toString().replace(/^\/|\/$/g, '')) // trim slashes
    .join('/')
    .replace(/^/, '/'); // ensure leading slash

export const paths = {
  auth: {
    token: () => join(API_BASE, 'token') + '/', //   /api/token/
    refresh: () => join(API_BASE, 'token/refresh') + '/', // /api/token/refresh/
  },
  organizations: {
    root: () => join(API_BASE, 'organizations') + '/',
    byId: (id: number) => join(API_BASE, 'organizations', id) + '/',
    current: () => join(API_BASE, 'organizations', 'current') + '/',
  },
  products: {
    root: () => join(API_BASE, 'products') + '/',
    byId: (id: number) => join(API_BASE, 'products', id) + '/',
    nested: (id: number, child: string) =>
      join(API_BASE, 'products', id, child) + (child.endsWith('/') ? '' : '/'),
    assets: (id: number) => join(API_BASE, 'products', id, 'assets') + '/',
    asset: (id: number, aid: number) => {
      const url = join(API_BASE, 'products', id, 'assets', aid) + '/';
      logDebug('Creating asset URL', {
        productId: id,
        assetId: aid,
        url,
      });
      return url;
    },
    attributes: (id: number) => join(API_BASE, 'products', id, 'attributes') + '/',
    attributeValue: (id: number, avId: number) =>
      join(API_BASE, 'products', id, 'attributes', avId) + '/',
  },
  attributes: {
    root: () => join(API_BASE, 'attributes') + '/',
    byId: (id: number) => join(API_BASE, 'attributes', id) + '/',
  },
  attributeGroups: {
    root: () => join(API_BASE, 'attribute-groups') + '/',
    byId: (id: number) => join(API_BASE, 'attribute-groups', id) + '/',
    addItem: (id: number) => join(API_BASE, 'attribute-groups', id, 'items') + '/',
    removeItem: (gid: number, itemId: number) =>
      join(API_BASE, 'attribute-groups', gid, 'items', itemId) + '/',
  },
  attributeSets: {
    byId: (id: number) => join(API_BASE, 'attribute-sets', id) + '/',
  },
  locales: {
    root: () => join(API_BASE, 'locales') + '/',
    byId: (id: number) => join(API_BASE, 'locales', id) + '/',
  },
  channels: {
    root: () => join(API_BASE, 'channels') + '/',
    byId: (id: number) => join(API_BASE, 'channels', id) + '/',
  },
  categories: {
    root: () => join(API_BASE, 'categories') + '/',
    byId: (id: number) => join(API_BASE, 'categories', id) + '/',
    products: (id: number) => join(API_BASE, 'categories', id, 'products') + '/',
  },
  families: {
    root: () => join(API_BASE, 'families') + '/',
    byId: (id: number) => join(API_BASE, 'families', id) + '/',
    attributeGroups: (id: number) => join(API_BASE, 'families', id, 'attribute-groups') + '/',
  },
  reports: {
    themes: () => join(API_BASE, 'reports/themes') + '/',
  },
  analytics: {
    completeness: () => join(API_BASE, 'analytics/completeness') + '/',
    readiness: () => join(API_BASE, 'analytics/readiness') + '/',
    enrichmentVelocity: (days: number = 30) =>
      join(API_BASE, 'analytics/enrichment-velocity') + `/?days=${days}`,
    localizationQuality: () => join(API_BASE, 'analytics/localization-quality') + '/', // Used for Localization Coverage report (frontend name updated, API path unchanged)
    changeHistory: (params?: {
      from?: string;
      to?: string;
      user?: number;
      users?: string[];
      entity?: string;
      actions?: string[];
    }) => {
      const url = join(API_BASE, 'analytics/change-history') + '/';
      if (!params) return url;

      const queryParams = new URLSearchParams();
      if (params.from) queryParams.append('from', params.from);
      if (params.to) queryParams.append('to', params.to);
      if (params.user) queryParams.append('user', params.user.toString());
      if (params.users && params.users.length > 0) {
        params.users.forEach(user => queryParams.append('username', user));
      }
      if (params.entity) queryParams.append('entity', params.entity);
      if (params.actions && params.actions.length > 0) {
        params.actions.forEach(action => queryParams.append('action', action));
      }

      const queryString = queryParams.toString();
      return queryString ? `${url}?${queryString}` : url;
    },
    locales: () => join(API_BASE, 'analytics/locales') + '/',
    channels: () => join(API_BASE, 'analytics/channels') + '/',
    categories: () => join(API_BASE, 'analytics/categories') + '/',
    families: () => join(API_BASE, 'analytics/families') + '/',
  },
  dashboard: () => join(API_BASE, 'dashboard') + '/',
} as const;

/**
 * Path to the localization quality analytics endpoint
 * @returns The full path to the localization quality endpoint
 */
export const analyticsLocalizationQuality = (): string =>
  join(API_BASE, 'analytics/localization-quality') + '/';
